import { useEffect, useState } from 'react';
import { Button, Input, SidePanel, Switch, useToast } from '../ui';
import { saveSweepSettings, type SweepService, type SweepSettings } from '../../lib/keywordSweep';

/**
 * A client's sweep settings: what they sell and where, how hard a keyword they can reach, how many
 * proposals a month, and the Ahrefs budget. A client with no settings row gets the table defaults
 * until this is saved once.
 */

interface SweepSettingsPanelProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
  settings: SweepSettings | null;
  onSaved: () => void | Promise<void>;
}

type ServiceDraft = { name: string; url: string; terms: string };

type NumberField = 'kd_margin' | 'kd_cap_override' | 'min_volume_a' | 'min_volume_b' | 'quota_a' | 'quota_b' | 'unit_cap' | 'sweep_day';

// The table defaults and check constraints (20260924000041_keyword_sweep.sql), so the form refuses
// what the database would.
const NUMBERS: Record<NumberField, { label: string; min: number; max: number; fallback: number | null; hint: string }> = {
  kd_margin: { label: 'KD margin', min: -100, max: 100, fallback: 10, hint: 'KD cap = DR + this' },
  kd_cap_override: { label: 'KD cap override', min: 0, max: 100, fallback: null, hint: 'Blank = DR + margin' },
  min_volume_a: { label: 'Min volume A', min: 0, max: 1_000_000, fallback: 20, hint: 'Searches / month' },
  min_volume_b: { label: 'Min volume B', min: 0, max: 1_000_000, fallback: 50, hint: 'Searches / month' },
  quota_a: { label: 'Quota A', min: 0, max: 50, fallback: 6, hint: 'Proposals per sweep' },
  quota_b: { label: 'Quota B', min: 0, max: 50, fallback: 12, hint: 'Proposals per sweep' },
  unit_cap: { label: 'Unit cap', min: 0, max: 200_000, fallback: 50_000, hint: 'Ahrefs units per sweep' },
  sweep_day: { label: 'Sweep day', min: 1, max: 28, fallback: 1, hint: 'Day of the month, 1–28' },
};
const NUMBER_ORDER = Object.keys(NUMBERS) as NumberField[];

const splitList = (text: string) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

function servicesOf(settings: SweepSettings | null): ServiceDraft[] {
  const list = Array.isArray(settings?.services) ? (settings.services as SweepService[]) : [];
  return list.map((s) => ({ name: s?.name ?? '', url: s?.url ?? '', terms: (s?.terms ?? []).join(', ') }));
}

function numbersOf(settings: SweepSettings | null): Record<NumberField, string> {
  return Object.fromEntries(
    NUMBER_ORDER.map((k) => {
      const value = settings ? settings[k] : NUMBERS[k].fallback;
      return [k, value == null ? '' : String(value)];
    })
  ) as Record<NumberField, string>;
}

/** "" → null for the one optional field; everything else a whole number within its range. */
function parseNumber(field: NumberField, raw: string): number | null | 'invalid' {
  const text = raw.replace(/,/g, '').trim();
  if (!text) return field === 'kd_cap_override' ? null : 'invalid';
  const n = Number(text);
  const { min, max } = NUMBERS[field];
  return Number.isInteger(n) && n >= min && n <= max ? n : 'invalid';
}

export default function SweepSettingsPanel({ open, onClose, clientId, clientName, settings, onSaved }: SweepSettingsPanelProps) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(true);
  const [services, setServices] = useState<ServiceDraft[]>([]);
  const [areas, setAreas] = useState('');
  const [numbers, setNumbers] = useState<Record<NumberField, string>>(() => numbersOf(null));
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Load from the row each time the panel opens, not on every realtime refresh of it, so a sweep
  // landing mid-edit doesn't wipe what the person typed.
  useEffect(() => {
    if (!open) return;
    setEnabled(settings?.enabled ?? true);
    setServices(servicesOf(settings));
    setAreas((settings?.service_areas ?? []).join(', '));
    setNumbers(numbersOf(settings));
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const invalid = NUMBER_ORDER.filter((k) => parseNumber(k, numbers[k]) === 'invalid');

  const setService = (index: number, patch: Partial<ServiceDraft>) =>
    setServices((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const save = async () => {
    const filled = services.filter((s) => s.name.trim() || s.url.trim() || s.terms.trim());
    if (filled.some((s) => !s.name.trim())) return setFormError('Every service needs a name.');
    if (invalid.length) return setFormError(`Check ${invalid.map((k) => NUMBERS[k].label).join(', ')}.`);

    const parsed = Object.fromEntries(NUMBER_ORDER.map((k) => [k, parseNumber(k, numbers[k])])) as Record<NumberField, number | null>;
    setSaving(true);
    setFormError(null);
    try {
      await saveSweepSettings(clientId, {
        enabled,
        services: filled.map((s) => ({ name: s.name.trim(), url: s.url.trim() || null, terms: splitList(s.terms) })),
        service_areas: splitList(areas),
        kd_margin: parsed.kd_margin as number,
        kd_cap_override: parsed.kd_cap_override,
        min_volume_a: parsed.min_volume_a as number,
        min_volume_b: parsed.min_volume_b as number,
        quota_a: parsed.quota_a as number,
        quota_b: parsed.quota_b as number,
        unit_cap: parsed.unit_cap as number,
        sweep_day: parsed.sweep_day as number,
      });
      toast.success('Sweep settings saved', clientName);
      await onSaved();
      onClose();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Settings were not saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      width={560}
      eyebrow={`Keyword sweep · ${clientName}`}
      title="Sweep settings"
      footer={
        <>
          <Button icon="check" onClick={save} disabled={saving || invalid.length > 0}>
            {saving ? 'Saving...' : 'Save settings'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {formError ? (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {formError}
          </div>
        ) : null}

        <div>
          <Switch checked={enabled} onChange={setEnabled} label="Sweep monthly" />
          <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '5px' }}>
            Off stops the schedule only. Run sweep now still works.
          </div>
        </div>

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Services</div>
          <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginBottom: '8px' }}>
            What they sell, the page that sells it, and the phrases that mean it. A keyword matching a service’s terms with buying or local intent is A tier.
            Left empty, the first sweep fills these from the KB.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {services.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: '6px',
                  padding: '8px',
                  border: '1px solid var(--border-hairline)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <Input fieldSize="sm" placeholder="Service name" aria-label="Service name" value={s.name} onChange={(e) => setService(i, { name: e.target.value })} />
                <Input fieldSize="sm" placeholder="/services/…" aria-label="Service page URL" value={s.url} onChange={(e) => setService(i, { url: e.target.value })} />
                <Button
                  size="sm"
                  variant="ghost"
                  icon="trash-2"
                  aria-label="Remove service"
                  title="Remove service"
                  onClick={() => setServices((list) => list.filter((_, j) => j !== i))}
                  style={{ padding: '0 8px' }}
                />
                <div style={{ gridColumn: '1 / -1' }}>
                  <Input
                    fieldSize="sm"
                    placeholder="Terms, comma-separated: panel upgrade, breaker box, …"
                    aria-label="Service terms"
                    value={s.terms}
                    onChange={(e) => setService(i, { terms: e.target.value })}
                  />
                </div>
              </div>
            ))}
            <span>
              <Button size="sm" variant="secondary" icon="plus" onClick={() => setServices((list) => [...list, { name: '', url: '', terms: '' }])}>
                Add service
              </Button>
            </span>
          </div>
        </div>

        <Input label="Service areas" placeholder="Denver, Aurora, Lakewood" value={areas} onChange={(e) => setAreas(e.target.value)} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' }}>
          {NUMBER_ORDER.map((k) => {
            const bad = invalid.includes(k);
            return (
              <div key={k}>
                <Input
                  label={NUMBERS[k].label}
                  inputMode="numeric"
                  placeholder={k === 'kd_cap_override' ? 'None' : undefined}
                  value={numbers[k]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNumbers((n) => ({ ...n, [k]: value }));
                  }}
                />
                <div style={{ fontSize: 'var(--text-2xs)', color: bad ? 'var(--status-warn)' : 'var(--text-faint)', marginTop: '4px' }}>
                  {bad ? `A whole number from ${NUMBERS[k].min.toLocaleString()} to ${NUMBERS[k].max.toLocaleString()}` : NUMBERS[k].hint}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SidePanel>
  );
}
